// AKSU Business School - LMS Authentication & RBAC Hierarchy
// Domain: businessschool.aksu.edu.ng
// British English throughout
import { FACULTY_LECTURERS, SEMESTER_COURSES } from './semesterCourseData';

export const USER_ROLES = {
  DIRECTOR: 'director',
  CONTENT_MANAGER: 'content-manager',
  FACULTY: 'faculty',
  STUDENT: 'student'
};

// Map each assigned faculty member into a first-class login credential
export const FACULTY_CREDENTIALS = FACULTY_LECTURERS.map((fac) => ({
  id: `user-${fac.id}`,
  facultyId: fac.id,
  role: USER_ROLES.FACULTY,
  label: `${fac.name} (${fac.assignedCourses.join(' & ')})`,
  name: fac.name,
  formalSalutation: fac.formalSalutation,
  title: fac.title,
  rank: fac.rank,
  qualifications: fac.qualifications,
  email: fac.email,
  demoUsername: fac.demoUsername,
  demoPassword: fac.demoPassword,
  department: fac.department,
  badge: `COURSE CHAIR · ${fac.assignedCourses.join(' & ')}`,
  leastPrivilegeTier: `Tier 2: Course-Scoped Academic Authority (${fac.assignedCourses.join(', ')})`,
  assignedCourses: fac.assignedCourses,
  avatar: fac.avatar,
  phone: fac.phone,
  office: fac.office,
  consultationHours: fac.consultationHours,
  bio: fac.bio,
  researchInterests: fac.researchInterests,
  workingPapers: fac.workingPapers,
  permissions: [
    `Course management and delivery for assigned courses (${fac.assignedCourses.join(', ')})`,
    "Targeted upload of syllabi, slides, case studies, video sessions, and datasets",
    "Continuous assessment grading, rubric evaluation, and executive student feedback",
    "Direct two-way messaging desk and broadcast announcements to enrolled scholars",
    "Research desk: manage working papers, publication preprints, and academic notes",
    "Restricted: Cannot alter global institutional fees, Bursary gates, or unassigned faculties' courses"
  ]
}));

export const AUTH_CREDENTIALS = [
  // 1. Executive Directorate (Global Admin)
  {
    id: 'user-director',
    role: USER_ROLES.DIRECTOR,
    label: "Executive Director (Global Admin)",
    name: "Dr Uwem Etim Uwah",
    title: "Acting Director, AKSU Business School",
    qualifications: "PhD, FCA, ACTI",
    email: "uwemuwah@aksu.edu.ng",
    demoUsername: "director@businessschool.aksu.edu.ng",
    demoPassword: "Director@AKSU2026",
    department: "Department of Accounting",
    badge: "GLOBAL ADMIN · SENATE DESK",
    leastPrivilegeTier: "Tier 1: Global Institutional & Senate Authority",
    permissions: [
      "Access school-wide financial settlements & Paystack gateway",
      "Executive Senate approvals & Doctoral defence clearances",
      "School-wide cohort analytics & NUC quality assurance",
      "Global administrative oversight across all LMS portals"
    ],
    avatar: "/director-uwah.png"
  },

  // 2. LMS Global Content Manager (Global Admin)
  {
    id: 'user-manager',
    role: USER_ROLES.CONTENT_MANAGER,
    label: "LMS Global Content Manager (Global Admin)",
    name: "Engr. Dr Inemesit Udofia",
    title: "Chief Academic Technology Officer & Global LMS Administrator",
    qualifications: "PhD, FNCS, MCPN",
    email: "content.manager@businessschool.aksu.edu.ng",
    demoUsername: "content.manager@businessschool.aksu.edu.ng",
    demoPassword: "LMSAdmin@AKSU2026",
    department: "Directorate of Academic Computing & E-Learning",
    badge: "GLOBAL LMS MASTER · CURRICULUM ARCHITECT",
    leastPrivilegeTier: "Tier 1: Global Curriculum & Content Governance",
    permissions: [
      "Master control over all Executive Programme curricula & course syllabi",
      "Manage and publish LMS module components (Slides, Cases, Videos)",
      "Audit and approve faculty learning material submissions",
      "Assign faculty chairs to executive course modules",
      "Enforce student cohort access rules and Bursary clearance gates",
      "System-wide activity logging and LMS security configuration"
    ],
    avatar: "https://images.unsplash.com/photo-1507152832244-10d45c7eda57?q=80&w=300&auto=format&fit=crop"
  },

  // 3. Default Faculty Anchor (Dr. Imoh Uford - BUS 901 & MAF 801)
  {
    ...FACULTY_CREDENTIALS[0],
    id: 'user-faculty', // Alias for backward compatibility
    label: "Executive Course Chair: Dr. Imoh Uford (BUS 901 & MAF 801)"
  },

  // 4. All 11 Dedicated Course Lecturer Personas
  ...FACULTY_CREDENTIALS,

  // 5. Enrolled Scholars (DBA, MAF, DAF, PhD, PGD)
  {
    id: 'user-student-dba',
    role: USER_ROLES.STUDENT,
    studentId: 'student-dba',
    label: "Executive Doctoral Scholar (DBA Track)",
    name: "Emem Udoh",
    matric: "AKSU/BS/2026/DBA/012",
    programme: "Doctor of Business Administration (DBA)",
    email: "emem.udoh@student.aksu.edu.ng",
    demoUsername: "emem.udoh@student.aksu.edu.ng",
    demoPassword: "Scholar@AKSU2026",
    badge: "ENROLLED SCHOLAR · DBA COHORT 2026",
    leastPrivilegeTier: "Tier 3: Enrolled Learner Access",
    cohort: "2025/2026 Executive Intake",
    clearanceStatus: "Bursary & Acceptance Cleared",
    enrolledCourseCodes: ["BUS 901", "BUS 911", "BUS 921", "BUS 931", "BUS 941"],
    permissions: [
      "Stream lecture videos and download DBA module slide decks",
      "Submit executive case studies and capstone research briefs",
      "View personal grades, attendance records, and faculty feedback",
      "Restricted: Cannot access other programmes, unapproved drafts, or grading desks"
    ],
    avatar: "https://images.unsplash.com/photo-1506277886164-e25aa3f4ef7f?q=80&w=300&auto=format&fit=crop"
  },
  {
    id: 'user-student-dba-2',
    role: USER_ROLES.STUDENT,
    studentId: 'student-dba-2',
    label: "Executive Doctoral Scholar: Obong Victor Okon (DBA)",
    name: "Obong Victor Okon",
    matric: "AKSU/BS/2026/DBA/015",
    programme: "Doctor of Business Administration (DBA)",
    email: "victor.okon@student.aksu.edu.ng",
    demoUsername: "victor.okon@student.aksu.edu.ng",
    demoPassword: "Scholar@AKSU2026",
    badge: "ENROLLED SCHOLAR · DBA COHORT 2026",
    leastPrivilegeTier: "Tier 3: Enrolled Learner Access",
    cohort: "2025/2026 Inaugural Intake",
    clearanceStatus: "Bursary & Acceptance Cleared",
    enrolledCourseCodes: ["BUS 901", "BUS 911", "BUS 921", "BUS 931", "BUS 941"],
    permissions: [
      "Stream lecture videos and download DBA module slide decks",
      "Submit executive case studies and capstone research briefs",
      "View personal grades, attendance records, and faculty feedback"
    ],
    avatar: "https://images.unsplash.com/photo-1621972660173-bb6dab9bd598?q=80&w=300&auto=format&fit=crop"
  },
  {
    id: 'user-student-maf',
    role: USER_ROLES.STUDENT,
    studentId: 'student-maf',
    label: "Executive Master Scholar: Bassey Edet (MAF)",
    name: "Bassey Edet",
    matric: "AKSU/BS/2026/MAF/018",
    programme: "Master of Accounting and Finance (MAF)",
    email: "bassey.edet@student.aksu.edu.ng",
    demoUsername: "bassey.edet@student.aksu.edu.ng",
    demoPassword: "Scholar@AKSU2026",
    badge: "ENROLLED SCHOLAR · MAF COHORT 2026",
    leastPrivilegeTier: "Tier 3: Enrolled Learner Access",
    cohort: "2025/2026 Inaugural Intake",
    clearanceStatus: "Bursary & Acceptance Cleared",
    enrolledCourseCodes: ["MAF 801", "ACC 801", "FIN 811", "ACC 811", "MAF 811"],
    permissions: [
      "Stream financial reporting and corporate valuation lecture recordings",
      "Download IFRS case packets, statistical models, and study notes",
      "Submit weekly module assessments and research proposals"
    ],
    avatar: "https://images.unsplash.com/photo-1531384441138-2736e62e0919?q=80&w=300&auto=format&fit=crop"
  },
  {
    id: 'user-student-maf-2',
    role: USER_ROLES.STUDENT,
    studentId: 'student-maf-2',
    label: "Executive Master Scholar: Blessing Eno Archibong (MAF)",
    name: "Blessing Eno Archibong",
    matric: "AKSU/BS/2026/MAF/022",
    programme: "Master of Accounting and Finance (MAF)",
    email: "blessing.archibong@student.aksu.edu.ng",
    demoUsername: "blessing.archibong@student.aksu.edu.ng",
    demoPassword: "Scholar@AKSU2026",
    badge: "ENROLLED SCHOLAR · MAF COHORT 2026",
    leastPrivilegeTier: "Tier 3: Enrolled Learner Access",
    cohort: "2025/2026 Inaugural Intake",
    clearanceStatus: "Bursary & Acceptance Cleared",
    enrolledCourseCodes: ["MAF 801", "ACC 801", "FIN 811", "ACC 811", "MAF 811"],
    permissions: [
      "Stream financial reporting and corporate valuation lecture recordings",
      "Download IFRS case packets, statistical models, and study notes",
      "Submit weekly module assessments and research proposals"
    ],
    avatar: "https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?q=80&w=300&auto=format&fit=crop"
  },
  {
    id: 'user-student-daf',
    role: USER_ROLES.STUDENT,
    studentId: 'student-daf',
    label: "Doctor of Accounting & Finance Fellow: Dr. Clementina Okon (DAF)",
    name: "Dr. Clementina Okon",
    matric: "AKSU/BS/2026/DAF/003",
    programme: "Doctor of Accounting and Finance (DAF)",
    email: "clementina.okon@student.aksu.edu.ng",
    demoUsername: "clementina.okon@student.aksu.edu.ng",
    demoPassword: "Scholar@AKSU2026",
    badge: "ENROLLED FELLOW · DAF COHORT 2026",
    leastPrivilegeTier: "Tier 3: Enrolled Learner Access",
    cohort: "2025/2026 Inaugural Intake",
    clearanceStatus: "Bursary & Acceptance Cleared",
    enrolledCourseCodes: ["DAF 901", "DAF 911", "ACC 911", "FIN 911", "DAF 941"],
    permissions: [
      "Access doctoral epistemology colloquia and financial econometric datasets",
      "Submit doctoral term papers and positive accounting theory models",
      "Engage directly with faculty chairs on thesis research methodology"
    ],
    avatar: "https://images.unsplash.com/photo-1573496799652-408c2ac9fe98?q=80&w=300&auto=format&fit=crop"
  },
  {
    id: 'user-student-daf-2',
    role: USER_ROLES.STUDENT,
    studentId: 'student-daf-2',
    label: "Doctor of Accounting & Finance Fellow: Kufre Mfoniso James (DAF)",
    name: "Kufre Mfoniso James",
    matric: "AKSU/BS/2026/DAF/007",
    programme: "Doctor of Accounting and Finance (DAF)",
    email: "kufre.james@student.aksu.edu.ng",
    demoUsername: "kufre.james@student.aksu.edu.ng",
    demoPassword: "Scholar@AKSU2026",
    badge: "ENROLLED FELLOW · DAF COHORT 2026",
    leastPrivilegeTier: "Tier 3: Enrolled Learner Access",
    cohort: "2025/2026 Inaugural Intake",
    clearanceStatus: "Bursary & Acceptance Cleared",
    enrolledCourseCodes: ["DAF 901", "DAF 911", "ACC 911", "FIN 911", "DAF 941"],
    permissions: [
      "Access doctoral epistemology colloquia and financial econometric datasets",
      "Submit doctoral term papers and positive accounting theory models",
      "Engage directly with faculty chairs on thesis research methodology"
    ],
    avatar: "https://images.unsplash.com/photo-1522529599102-193c0d76b5b6?q=80&w=300&auto=format&fit=crop"
  },
  {
    id: 'user-student-phd',
    role: USER_ROLES.STUDENT,
    studentId: 'student-phd',
    label: "Doctoral Research Fellow (PhD Track)",
    name: "Idorenyin Bassey",
    matric: "AKSU/BS/2026/PHD/004",
    programme: "PhD in Business Administration",
    email: "idorenyin.bassey@student.aksu.edu.ng",
    demoUsername: "idorenyin.bassey@student.aksu.edu.ng",
    demoPassword: "Scholar@AKSU2026",
    badge: "ENROLLED SCHOLAR · PHD COHORT 2026",
    leastPrivilegeTier: "Tier 3: Enrolled Learner Access",
    cohort: "2025/2026 Executive Intake",
    clearanceStatus: "50% Session Tuition Paid & Cleared",
    enrolledCourseCodes: ["BUS 901", "BUS 911", "BUS 921"],
    permissions: [
      "Access doctoral research seminars, econometrics modules, and readings",
      "Submit doctoral dissertation chapters and proposal drafts",
      "View supervisor notes and colloquium dates"
    ],
    avatar: "https://images.unsplash.com/photo-1573497019236-17f8177b81e8?q=80&w=300&auto=format&fit=crop"
  },
  {
    id: 'user-student-pgd',
    role: USER_ROLES.STUDENT,
    studentId: 'student-pgd',
    label: "Postgraduate Diploma Scholar (PGD Track)",
    name: "Itoro Akpan",
    matric: "AKSU/BS/2026/PGD/055",
    programme: "Postgraduate Diploma in Business Administration (PGD)",
    email: "itoro.akpan@student.aksu.edu.ng",
    demoUsername: "itoro.akpan@student.aksu.edu.ng",
    demoPassword: "Scholar@AKSU2026",
    badge: "ENROLLED SCHOLAR · PGD COHORT 2026",
    leastPrivilegeTier: "Tier 3: Enrolled Learner Access",
    cohort: "October 2026 Executive Intake",
    clearanceStatus: "Bursary & Acceptance Cleared",
    enrolledCourseCodes: ["BUS 901", "MAF 801"],
    permissions: [
      "Access foundational management coursework and quantitative study packs",
      "Submit weekly module assessments and business case reviews",
      "Track pathway progress toward executive masters progression"
    ],
    avatar: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?q=80&w=300&auto=format&fit=crop"
  }
];

// Master Course & Module Taxonomy for Curriculum CMS
export const LMS_MASTER_CURRICULUM = SEMESTER_COURSES.map(course => ({
  courseCode: course.code,
  courseTitle: course.title,
  programme: course.programmeName,
  programmeId: course.programmeId,
  leadFaculty: course.lecturer?.name || "Assigned Faculty",
  semester: course.semester,
  targetCohort: "2025/2026 & 2026/2027 Executive Intake",
  enrolledStudentCount: course.enrolledStudents?.length || 5,
  modules: course.modules.map((m, idx) => ({
    id: `MOD-${course.code.replace(/\s+/g, '-')}-0${idx + 1}`,
    number: idx + 1,
    title: m.title,
    status: m.status,
    components: [
      { slot: "slides", label: "Executive Lecture Slides (PDF/PPT)", file: `${course.code.replace(/\s+/g, '')}_Mod${idx + 1}_Slides.pdf`, size: "4.5 MB", date: "15 Oct 2026", status: "Published" },
      { slot: "case-study", label: "Harvard / African Industry Case", file: `${course.code.replace(/\s+/g, '')}_Executive_Case.pdf`, size: "2.8 MB", date: "16 Oct 2026", status: "Published" },
      { slot: "video", label: "Executive Video Recording (HD)", file: `${course.code.replace(/\s+/g, '')}_Session_Recording.mp4`, size: "520 MB", date: "17 Oct 2026", status: "Published" },
      { slot: "assignment", label: "Assessment Brief", file: `${course.code.replace(/\s+/g, '')}_Assessment_01.pdf`, size: "750 KB", date: "18 Oct 2026", status: "Published" }
    ]
  }))
}));
